import React, { useEffect, useMemo } from 'react';
import ReactDom from 'react-dom';
import { Link, useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { formattedPathKey } from 'helpers/formattedPathKey';
import { getPreviousLocation, getReferrerLocation } from 'helpers/navigationHistory';

import { get, SessionStorageManager, SiteConfigManager, useApplicationState } from '@atlas/core';
import {
  availableClientProfileRoutes,
  availablePaymentRoutes,
  CLIENT_PROFILE_COMPONENT_TYPES,
  clientProfileRoutes,
  COMPONENT_TYPES,
  paymentRoutes,
  staticRoutes,
} from '@atlas/routing';
import { Icon, Label, ScrollableContainer } from '@atlas/ui';

import { ACCOUNT_STATUSES, POSTHOG_SESSION_KEYS } from 'common/constants';
import ClientProfileTabNavigation from 'components/ClientProfile/ClientProfileTabNavigation';
import ShortFooter from 'components/Footer/ShortFooter';

import PaymentRouter from './PaymentRouter';

const { CLIENT_PROFILE } = COMPONENT_TYPES;

const { PAYMENT } = CLIENT_PROFILE_COMPONENT_TYPES;
const { payment } = clientProfileRoutes;

const Payment = () => {
  const preselectedPaymentMethod = SiteConfigManager.getConfig(
    'config.config.mobile.preselected_payment_method',
  );
  const { pathname } = useLocation();

  const { account, authenticated } = useApplicationState();
  const history = useHistory();

  useEffect(() => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      history.replace(staticRoutes.signup);
    }
  }, [account.status, history]);

  if (!SessionStorageManager.getItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER)) {
    const depositPageRefer =
      getPreviousLocation()?.pathname ?? getReferrerLocation()?.pathname ?? window.location.pathname;

    SessionStorageManager.setItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER, depositPageRefer);
  }

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const clientProfileTabs = get(SiteConfigManager.getComponentOptions(CLIENT_PROFILE), 'tabs');
  const paymentOptions = clientProfileTabs.find(({ type }) => type === PAYMENT);
  const { isExpandedOnMobile, tabs = [] } = paymentOptions;

  const activeTabs = useMemo(() => tabs.filter(tab => tab.isActive), [tabs]);

  const className = cx('payments-list', isExpandedOnMobile && 'disable-top-navigation');

  const goBackMessage = isExpandedOnMobile
    ? formattedPathKey(pathname, clientProfilePath)
    : availableClientProfileRoutes[PAYMENT].name;

  const isDepositModalOpen = useMemo(() => {
    const pattern = preselectedPaymentMethod
      ? `\\w+\\/${paymentRoutes.deposit}`
      : `\\w+\\/${paymentRoutes.deposit}\\/\\d+`;
    const regex = new RegExp(pattern);

    return regex.test(pathname);
  }, [pathname, preselectedPaymentMethod]);

  if (!authenticated) return null;

  return ReactDom.createPortal(
    <div className="profile-wrapper-absolute payment-container">
      {!isDepositModalOpen && (
        <Link className="back-to-container" to={clientProfilePath}>
          <Icon className="icon-back" name="back" />
          <Label className="title-text" message={goBackMessage} />
        </Link>
      )}
      <ClientProfileTabNavigation
        availableRoutes={availablePaymentRoutes}
        clientProfilePath={clientProfilePath}
        isExpandedOnMobile={isExpandedOnMobile}
        tabPath={payment}
        tabs={activeTabs}
      />
      <ScrollableContainer className={className} id="content">
        <PaymentRouter tabs={activeTabs} />
        <ShortFooter />
      </ScrollableContainer>
    </div>,
    document.body,
  );
};

export default Payment;
